import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _groupBy from "lodash/groupBy";
var _class;
import { action, computed } from 'mobx';
import { AssetType } from '@kentik/ui-shared/labels/lib';
import { RBACAssetTagPermission } from '@kentik/ui-shared/rbac/constants';
import AssetTagCollection from './AssetTagCollection';
export let AssetTagsStore = (_class = class AssetTagsStore {
  constructor() {
    this.store = void 0;
    this.collection = new AssetTagCollection();
  }
  initialize() {
    if (this.canView && !this.store.$app.isSubtenant) {
      this.collection.fetch();
    }
  }
  get enabled() {
    return this.store.$auth.hasPermission('assetTags.enabled', {
      overrideForSudo: false
    });
  }
  get canCreate() {
    return this.enabled && this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.CREATE]);
  }
  get canView() {
    return this.enabled && this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.READ]);
  }
  get canUpdate() {
    return this.enabled && this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.UPDATE]);
  }
  get canDelete() {
    return this.enabled && this.store.$auth.hasRbacPermissions([RBACAssetTagPermission.DELETE]);
  }
  get colorEnabled() {
    return false;
  }

  /**
   * Returns titles/descriptions for asset tags navigation items based on feature flag
   */
  getNavLabels() {
    if (this.canView) {
      return {
        title: 'Labels & Tags',
        description: 'Create labels and tags that can be associated with devices, policies, synthetics agents, and synthetics tests'
      };
    }
    return {
      title: 'Labels',
      description: 'Create labels that can be associated with devices, policies, synthetics agents, and synthetics tests'
    };
  }

  /**
   * Fetches collections for provided asset types, defaulting to all
   * @param options - An object containing an optional array of asset types to fetch.
   * @returns A promise that resolves when all specified asset collections have been fetched.
   */
  fetchAssetCollections({
    assetTypes = Object.values(AssetType)
  } = {}) {
    return Promise.all(assetTypes.map(assetType => {
      const {
        collection
      } = this.getAssetCollection(assetType);
      if (collection && !collection.hasFetched && !collection.loading) {
        return collection.fetch();
      }
      return null;
    }));
  }

  /**
   * Retrieves the asset collection lookup for a given asset type.
   * This is used for managing each asset's collection selected/filter state.
   * It's the selection source for creating value mappings on an asset tag.
   * @param assetType The type of asset for which to retrieve the collection lookup.
   * @returns An object containing the collection and the name field for the specified asset type.
   */
  getAssetCollection(assetType) {
    const assetCollectionLookup = {
      [AssetType.DEVICE]: {
        collection: this.store.$devices.collection,
        nameField: 'device_name'
      },
      [AssetType.BOARD]: {
        collection: this.store.$boards.collection,
        nameField: 'title'
      },
      [AssetType.DASHBOARD]: {
        collection: this.store.$dashboards.collection,
        nameField: 'dash_title'
      },
      [AssetType.SAVED_VIEW]: {
        collection: this.store.$savedViews.collection,
        nameField: 'view_name'
      },
      [AssetType.POLICY]: {
        collection: this.store.$alerting.policyCollection,
        nameField: 'name'
      },
      [AssetType.CREDENTIAL_VAULT]: {
        collection: this.store.$credentials.groupedCollection,
        nameField: 'name'
      },
      [AssetType.PACKAGE]: {
        collection: this.store.$mkp.packages,
        nameField: 'name'
      },
      [AssetType.TENANT]: {
        collection: this.store.$mkp.tenants,
        nameField: 'name'
      },
      [AssetType.SYNTH_AGENT]: {
        collection: this.store.$syn.agents,
        nameField: 'agent_alias'
      },
      [AssetType.SYNTH_TEST]: {
        collection: this.store.$syn.tests,
        nameField: 'config.name'
      }
    };
    return assetCollectionLookup[assetType] || {};
  }

  /**
   * Retrieves the asset type corresponding to a given collection.
   * @param collection The collection for which to determine the asset type.
   * @returns The asset type associated with the collection, or null if not found.
   */
  getAssetTypeByCollection(collection) {
    const assetType = Object.values(AssetType).find(type => {
      const {
        collection: assetCollection
      } = this.getAssetCollection(type);
      return assetCollection === collection;
    });
    return assetType || null;
  }

  /**
   * Retrieves the asset model (DeviceModel etc) for a specific asset type and ID.
   * @param An object containing the asset type and asset ID.
   * @returns The asset model, or null if it cannot be found.
   */
  getAssetModel({
    assetType,
    assetId
  }) {
    const {
      collection
    } = this.getAssetCollection(assetType);
    let model = null;
    if (collection) {
      model = collection.get(assetId) || null;
    }
    return model;
  }

  /**
   * Retrieves the display name for a specific asset based on its type and ID.
   * This is useful for showing human-readable names for assets in the UI, especially when creating value mappings for asset tags.
   * @param An object containing the asset type and asset ID.
   * @returns The display name of the asset, or null if it cannot be found.
   */
  getAssetName({
    assetType,
    assetId
  }) {
    const model = this.getAssetModel({
      assetType,
      assetId
    });
    let name = null;
    if (model) {
      const {
        nameField
      } = this.getAssetCollection(assetType);
      if (nameField) {
        name = model.get(nameField) || null;
      }
    }
    return name;
  }

  /**
   * Computes the total number of selected assets across all asset types and indicates if any of the collections are currently loading.
   * Used for display in the manage mappings dialog footer.
   * @returns An object containing the total count of selected assets and a boolean indicating if any collection is loading.
   */
  get totalSelectedAssets() {
    return Object.values(AssetType).reduce((acc, assetType) => {
      const {
        collection
      } = this.getAssetCollection(assetType) || {};
      if (collection) {
        acc.count += collection.selectedArray.length;
        if (collection.loading) {
          acc.loading = true;
        }
      }
      return acc;
    }, {
      count: 0,
      loading: false
    });
  }

  /**
   * Provides a list of options for the DeviceSelector component.
   */
  get deviceOptions() {
    if (!this.collection.hasFetched) {
      return [];
    }
    const options = [];
    this.collection.get().forEach(model => {
      const deviceItems = (model.get('items') || []).filter(item => item.entity_type === AssetType.DEVICE);
      if (deviceItems.length > 0) {
        Object.entries(_groupBy(deviceItems, 'value')).forEach(([value, items]) => {
          options.push({
            label: `${model.keyName} = ${value}`,
            value: `${model.id}:${value}`,
            numDevices: items.length,
            // @ts-expect-error we're not adding this to the generic RelatedAssetCollection type
            devices: items.map(item => this.store.$devices.activeDeviceSummariesById[item.entity_id])
          });
        });
      }
    });
    return options;
  }

  /**
   * Exposes DeviceSelector options as a dictionary for lookup by value.
   */
  get deviceOptionsById() {
    const options = {};
    this.deviceOptions.forEach(option => {
      options[option.value] = option;
    });
    return options;
  }

  /**
   * Merges the current selections of assets with the filtered items in the collection for a specific asset type.
   * If 'add' is true, the filtered items are added to the current selections.
   * If 'add' is false, the filtered items are removed from the current selections.
   * @param param0 An object containing the 'add' flag and the 'assetType' to merge selections for.
   * @returns void
   */
  mergeSelections({
    add,
    assetType
  }) {
    const {
      collection
    } = this.getAssetCollection(assetType) || {};
    let nextSelected = [];
    // get the current selections
    const currentSelections = collection?.selectedArray || [];
    // get the currently filtered items
    const filteredModels = collection?.models.filter(model => model.isSelectable) || [];
    if (add) {
      // merge in the newly selected values
      nextSelected = Array.from(new Set([...currentSelections, ...filteredModels]));
    } else {
      // deselect the filtered items
      nextSelected = currentSelections.filter(model => !filteredModels.includes(model));
    }
    if (collection) {
      collection.selected = nextSelected;
    }
  }

  /**
   * Get asset tags optionally filtered by entity types.
   * @param options - An object containing optional entity types to filter by.
   * @returns An array of asset tags matching the specified entity types.
   */
  getAssetTags({
    entityTypes
  } = {}) {
    return this.collection.models.filter(model => {
      let matches = true;
      if (Array.isArray(entityTypes)) {
        matches = entityTypes.some(entityType => model.entityUsage.has(entityType));
      }
      return matches;
    });
  }

  /**
   * Get asset tag options optionally filtered by entity types.
   * This is used for the dropdown options in admin table asset tag sidebar filters.
   * @param options - An object containing optional entity types to filter by.
   * @returns An array of asset tag options matching the specified entity types.
   */
  getOptions({
    entityTypes
  } = {}) {
    return [{
      value: '-',
      label: 'None'
    }].concat(this.getAssetTags({
      entityTypes
    }).map(model => ({
      value: model.get('id'),
      label: model.get('display_name') || model.get('name'),
      bg: model.get('color')
    })).sort((a, b) => a.label.localeCompare(b.label)));
  }

  /**
   * Filter a model based on selected asset tag values and logic.
   * @param options.relatedAssetModel - The model to be filtered.
   * @param options.assetType - The type of the entity.
   * @param options.tagId - The ID of the asset tag to filter by.
   * @param options.values - The selected asset tag values.
   * @returns A boolean indicating if the model matches the filter criteria.
   */
  multiSelectModelFilter({
    relatedAssetModel,
    assetType,
    tagId,
    values
  }) {
    if (this.collection.loading) {
      // pass through models when we don't have asset tag values to compare against
      return true;
    }
    return values.some(value => {
      if (value === '-') {
        // 'unassigned' means there are no values assigned for the given tag
        const tagModel = this.collection.get(tagId);
        return !(tagModel?.get('items') || []).some(item => item.entity_type === assetType && item.entity_id === relatedAssetModel.id);
      }
      return !!this.collection.get().find(model => model.id === tagId && model.isTagged({
        relatedAssetModel,
        assetType,
        value
      }));
    });
  }

  /**
   * Converts an array of asset tag datasource strings to an array of human-readable labels.
   * Any missing or unmatched tag ids are thrown out.
   * ex: ['4:green', '5:vanilla', '4:pink', undefined] -> ['Color = green, pink', 'Flavor = vanilla']
   */
  toDatasourceLabels(datasources) {
    const labels = Object.create(null);
    if (Array.isArray(datasources)) {
      datasources.forEach(datasource => {
        const [tag_id, ...valueParts] = datasource?.split(':') || [];
        const value = valueParts.join(':'); // handle embedded colons
        const tagModel = this.collection.get(tag_id);
        const keyName = tagModel?.keyName || `${tag_id} (removed)`;
        if (keyName && value) {
          labels[keyName] = labels[keyName] || [];
          labels[keyName].push(value);
        }
      });
    }
    return Object.entries(labels).map(([tag_id, values]) => `${tag_id} = ${values.join(', ')}`);
  }
}, _applyDecoratedDescriptor(_class.prototype, "enabled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "enabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canCreate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canCreate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canView", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canView"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canUpdate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canDelete", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canDelete"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "colorEnabled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "colorEnabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "totalSelectedAssets", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "totalSelectedAssets"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deviceOptions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "deviceOptions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deviceOptionsById", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "deviceOptionsById"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "mergeSelections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "mergeSelections"), _class.prototype), _class);
export default new AssetTagsStore();